// Server-side 301 redirect from legacy numeric ids to Strapi v5 documentIds.
// Strapi v4 used auto-increment numeric ids in URLs; v5 single-entity lookups
// require the new string documentId. The numeric id column still exists in
// the DB and is filterable via REST, so map old -> new and 301 to keep
// external links / SEO working.

// SSR-only middleware: use the in-cluster URL when available; fall back to the
// browser-facing URL for single-URL deployments (on-prem, local dev).
const cmsURL = process.env.STRAPI_URL_INTERNAL || process.env.STRAPI_URL || 'https://motec.com.au/hessian'
const isNumeric = v => typeof v === 'string' && /^\d+$/.test(v)

async function lookupDocumentId ($axios, plural, numericId) {
  try {
    const res = await $axios.$get(
      `${cmsURL}/api/${plural}`,
      {
        params: {
          'filters[id][$eq]': numericId,
          'fields[0]': 'documentId',
          'pagination[limit]': 1
        }
      }
    )
    const item = res && res.data && res.data[0]
    return item ? item.documentId : null
  } catch (e) {
    return null
  }
}

function buildQuery (query, overrides) {
  const merged = { ...query, ...overrides }
  const parts = Object.keys(merged)
    .filter(k => merged[k] !== undefined && merged[k] !== null && merged[k] !== '')
    .map(k => encodeURIComponent(k) + '=' + encodeURIComponent(merged[k]))
  return parts.length ? '?' + parts.join('&') : ''
}

export default async function ({ route, redirect, $axios, req }) {
  // Detect any legacy numeric id in the affected URL shapes. If we're on
  // the client (a <nuxt-link> navigation), don't try to call Strapi REST
  // directly — that would be cross-origin (CORS) and fail silently. Instead
  // force a full page load so the SSR middleware can issue the 301.
  const needsLegacyLookup =
    /^\/dealer\/\d+\/?$/.test(route.path) ||
    /^\/faq\/\d+\/?$/.test(route.path) ||
    (/^\/products\/[^/]+\/?$/.test(route.path) && isNumeric(route.query.catId) && route.query.catId !== '0') ||
    (/^\/products\/category\/[^/]+\/[^/]+\/?$/.test(route.path) && isNumeric(route.query.id))

  if (!needsLegacyLookup) return

  if (process.client) {
    window.location.assign(route.fullPath)
    return
  }

  // SSR path: look up documentId via Strapi REST and 301 redirect.
  // /dealer/:id
  let m = route.path.match(/^\/dealer\/([^/]+)\/?$/)
  if (m && isNumeric(m[1])) {
    const docId = await lookupDocumentId($axios, 'dealers', m[1])
    if (docId) return redirect(301, `/dealer/${docId}`)
    return // fall through -> page will 404 cleanly
  }

  // /faq/:id
  m = route.path.match(/^\/faq\/([^/]+)\/?$/)
  if (m && isNumeric(m[1])) {
    const docId = await lookupDocumentId($axios, 'faq-topics', m[1])
    if (docId) return redirect(301, `/faq/${docId}`)
    return
  }

  // /products/:slug?catId=N  (catId='0' is a legitimate "no category" sentinel)
  if (/^\/products\/[^/]+\/?$/.test(route.path) &&
      isNumeric(route.query.catId) && route.query.catId !== '0') {
    const docId = await lookupDocumentId($axios, 'product-categories', route.query.catId)
    if (docId) {
      return redirect(301, route.path + buildQuery(route.query, { catId: docId }))
    }
    return
  }

  // /products/category/:catslug/:slug?id=N
  if (/^\/products\/category\/[^/]+\/[^/]+\/?$/.test(route.path) &&
      isNumeric(route.query.id)) {
    const docId = await lookupDocumentId($axios, 'showcases', route.query.id)
    if (docId) {
      return redirect(301, route.path + buildQuery(route.query, { id: docId }))
    }
  }
}
